import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Stock : famille "quantité disponible d'un article à un emplacement".
 *
 * Niveau de stock d'un `product` à un `place` donné, avec seuil d'alerte
 * (réassort) et part réservée. Construite sur le modèle "famille + type" de la
 * bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète de la ligne de stock : il
 * connaît la famille générique `stock`. Le vocabulaire est porté par le champ
 * `type` (chaîne libre déclarée par l'app : "quant", "serie"...).
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `productId` (l'article) et `placeId` (l'emplacement) sont le CŒUR de la
 * famille : un stock est toujours la quantité d'un article à un endroit, donc
 * les deux sont requis (comme eventId/contactId pour registration). `lotId` (le
 * lot ou la série suivie) et `orderId` (la pièce pour laquelle la part réservée
 * est bloquée) sont optionnels. Les mouvements de stock (`stock-movement`) ne
 * vivent PAS ici : chaque mouvement porte SON `stockId` (relation un à plusieurs).
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille stock (hors champs standard du socle). */
export const stockCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "quant", "serie"). */
  type: z.string().min(1),
  /** Article suivi (référence `product`, cœur de la famille). Requis. */
  productId: z.string().min(1),
  /** Emplacement où l'article est stocké (référence `place`, cœur de la famille). Requis. */
  placeId: z.string().min(1),
  /** Quantité physiquement présente à l'emplacement. Optionnel. */
  quantityOnHand: z.number().int().optional(),
  /** Quantité déjà réservée (bloquée) sur le stock présent. Optionnel. */
  quantityReserved: z.number().int().optional(),
  /** Seuil d'alerte : niveau qui déclenche un réassort. Optionnel. */
  reorderPoint: z.number().int().optional(),
  /** Unité de mesure (ex. "piece", "kg", "L"). Optionnel. */
  unit: z.string().min(1).optional(),
  /** Lot ou série suivie (référence `lot`). Optionnel. */
  lotId: z.string().min(1).optional(),
  /** Pièce pour laquelle la part réservée est bloquée (référence `order`). Optionnel. */
  orderId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille stock (champs standard + champs métier). */
export const StockSchema = z.object({
  ...baseEntityFields,
  ...stockCoreFields,
});

/** Type d'un stock. */
export type Stock = z.infer<typeof StockSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const StockCreateInputSchema = z.object({
  ...stockCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un stock. */
export type StockCreateInput = z.infer<typeof StockCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const StockUpdateInputSchema = z
  .object({ ...stockCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un stock. */
export type StockUpdateInput = z.infer<typeof StockUpdateInputSchema>;
